import React, { useState, useEffect } from "react";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import DataTable from "../../../components/Content/DataTable";

// Date & Time
import Datetime from "react-datetime";
import moment from "moment";

// Utils
import { getAlert, getData, updateData } from "../../../../utils/SamuiUtils";
import {
  formatStringDateToDate,
  formatThaiDateUi,
} from "../../../../utils/DateUtils";

// Components Date Filter
import DateFilterTable from "../../../components/Content/DateFilterTable";

function Main({
  masterList,
  detailList,
  statusColours,
  name,
  onPageInsert,
  onRowSelected,
  getReport,
}) {
  const [dataMasterList, setDataMasterList] = useState([]);
  const [dataDetailList, setDataDetailList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(16);
  const [countWaitApprove, setCountWaitApprove] = useState(0);
  const [countApprove, setCountApprove] = useState(0);
  const [countNotApprove, setCountNotApprove] = useState(0);
  const [countCancel, setCountCancel] = useState(0);

  // ใช้สำหรับค้นหาเอกสาร
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  // console.debug("filteredItems",filteredItems)

  // Filter by date & Sort by field
  const [startDate, setStartDate] = useState(
    moment().startOf("month").add(543, 'year').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(moment().add(543, 'year').format('YYYY-MM-DD'));
  const [sortOrder, setSortOrder] = useState("");
  const [sortField, setSortField] = useState("");
  const [selectedDateType, setSelectedDateType] = useState("1");
  const [allDates, setAllDates] = useState(false);

  useEffect(() => {
    initialize();
  }, [masterList, detailList]);

  useEffect(() => {
    setCurrentPage(1);
    filterItems();
  }, [searchTerm, dataMasterList]);

  // Remove reset of sortField on date change to persist user sorting preference
  // useEffect(() => {
  //   setSortField("");
  // }, [startDate, endDate]);

  useEffect(() => {
    // ตั้งค่าเริ่มต้นเมื่อ component โหลด
    setSortField("Doc_Date");
    setSortOrder("desc");
  }, []);

  useEffect(() => {
    if (sortField) {
      setFilteredItems((prevItems) => {
        return [...prevItems].sort((a, b) => {
          let aValue = a[sortField] || "";
          let bValue = b[sortField] || "";

          if (sortField === "Doc_No") {
            // ลบตัวอักษรนำหน้าทั้งหมดและแปลงเป็นตัวเลข
            const numA = parseInt(aValue.replace(/^\D+/g, ""), 10) || 0;
            const numB = parseInt(bValue.replace(/^\D+/g, ""), 10) || 0;

            return sortOrder === "asc" ? numA - numB : numB - numA;
          }

          if (typeof aValue === "string" && typeof bValue === "string") {
            return sortOrder === "asc"
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }

          return 0;
        });
      });
    }
  }, [sortField, sortOrder, dataMasterList]);

  const initialize = async () => {
    try {
      console.debug("🔄 กำลังโหลดข้อมูล masterList...");

      if (!masterList || masterList.length === 0) {
        console.warn("⚠️ masterList ว่าง หรือไม่ได้โหลดข้อมูลใหม่");
        return;
      }

      // ดึงข้อมูลรูปภาพ
      const image = await getData(
        "Upload_File_List",
        `DocCode_ID = 2`,
        `Comp_Id = ${masterList.at(0).Comp_Id}`
      );

      // รวมข้อมูล `masterList` กับ `image`
      const updatedMasterList = masterList.map((item) => {
        const matchedImages = image
          .filter((img) => img.Doc_ID === item.Doc_ID)
          .map((img) => img.File_Name);
        return { ...item, File_Name: matchedImages };
      });

      setDataMasterList(updatedMasterList);
      setFilteredItems(updatedMasterList);

      console.debug("✅ อัปเดตข้อมูลใหม่สำเร็จ:", updatedMasterList);

      // คำนวณสถานะ
      let statusCounts = {
        รออนุมัติ: 0,
        อนุมัติ: 0,
        ไม่อนุมัติ: 0,
        ยกเลิก: 0,
      };

      updatedMasterList.forEach((data) => {
        if (data.DocStatus_Name) {
          statusCounts[data.DocStatus_Name] =
            (statusCounts[data.DocStatus_Name] || 0) + 1;
        }
      });

      setCountWaitApprove(statusCounts["รออนุมัติ"]);
      setCountApprove(statusCounts["อนุมัติ"]);
      setCountNotApprove(statusCounts["ไม่อนุมัติ"]);
      setCountCancel(statusCounts["ยกเลิก"]);
      setCurrentPage(1); // รีเซ็ตหน้า
    } catch (error) {
      console.error("❌ initialize() ล้มเหลว:", error);
      getAlert("FAILED", error.message);
    }
  };

  const filterItems = () => {
    if (searchTerm === "") {
      setFilteredItems(dataMasterList);
    } else {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const filtered = dataMasterList.filter((item) =>
        Object.values(item).some(
          (value) =>
            typeof value === "string" &&
            value.toLowerCase().includes(lowercasedSearchTerm)
        )
      );
      setFilteredItems(filtered);
    }
  };

  // filter วันที่
  const filterItemsByDate = async (type) => {
    // If selecting all dates, don't filter on client
    if (allDates) {
      setFilteredItems(dataMasterList);
      return;
    }
    let filteredByDate = dataMasterList;

    if (startDate && endDate) {
      // แปลง startDate และ endDate จาก YYYY-MM-DD (พ.ศ.) เป็น moment และลบ 543 ปีกลับเป็น ค.ศ.
      const start = moment(startDate, "YYYY-MM-DD").subtract(543, 'year');
      const end = moment(endDate, "YYYY-MM-DD").subtract(543, 'year').endOf("day");

      filteredByDate = dataMasterList.filter((item) => {
        // แปลงวันที่จาก ISO string (2025-09-19T00:00:00.000Z) เป็น moment
        const itemDate =
          type === "documentDate"
            ? moment(item.Doc_Date)
            : type === "dueDate"
            ? moment(item.Doc_DueDate)
            : null;
        if (!itemDate || !itemDate.isValid()) return false;

        const isBetween = itemDate.isBetween(start, end, null, "[]");
        return isBetween;
      });
    }

    // Apply current sorting if set
    if (sortField) {
      filteredByDate.sort((a, b) => {
        let aValue = a[sortField] || "";
        let bValue = b[sortField] || "";

        if (sortField === "Doc_No") {
          const numA = parseInt(aValue.replace(/^\D+/g, ""), 10) || 0;
          const numB = parseInt(bValue.replace(/^\D+/g, ""), 10) || 0;
          return sortOrder === "asc" ? numA - numB : numB - numA;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        return 0;
      });
    }

    setFilteredItems(filteredByDate);
  };

  //ค้นหาวันที่และเลขเอกสาร
  const handleSearch = () => {
    const type = selectedDateType === "1" ? "documentDate" : "dueDate";
    filterItemsByDate(type);
  };

  // Clear search and date filters
  const handleClear = () => {
    setSearchTerm("");
    setStartDate(moment().startOf("month").add(543, 'year').format('YYYY-MM-DD'));
    setEndDate(moment().add(543, 'year').format('YYYY-MM-DD'));
    setSelectedDateType("1");
    setFilteredItems(dataMasterList);
    setSortField("");
    setSortOrder("desc");
  };

  const handleSort = () => {
    if (!sortField) return;

    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);

    const sortedItems = [...filteredItems].sort((a, b) => {
      let aValue = a[sortField] || "";
      let bValue = b[sortField] || "";

      if (sortField === "Doc_No") {
        // ลบตัวอักษรนำหน้าทั้งหมดและแปลงเป็นตัวเลข
        const numA = parseInt(aValue.replace(/^\D+/g, ""), 10) || 0;
        const numB = parseInt(bValue.replace(/^\D+/g, ""), 10) || 0;

        return newSortOrder === "asc" ? numA - numB : numB - numA;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return newSortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    setFilteredItems(sortedItems);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const updateStatus = async (Doc_No) => {
    try {
      const newStatus = 4;
      const values = { Doc_Status: newStatus };
      const where = { Doc_No };

      console.debug("📌 กำลังอัปเดต PO_H:", values, where);

      const res = await updateData("PO_H", values, where);
      console.debug("🔍 ผลลัพธ์ updateData:", res);

      if (res && res.rowsAffected && res.rowsAffected[0] > 0) {
        console.debug("✅ อัปเดตสถานะสำเร็จ! กำลังโหลดข้อมูลใหม่...");
        setDataMasterList([]);
        setFilteredItems([]);

        await new Promise((resolve) => setTimeout(resolve, 100));

        setTimeout(() => {
          window.location.reload();
        }, 300);
      } else {
        throw new Error(
          "⚠️ ไม่สามารถอัปเดตสถานะได้ หรือไม่มีข้อมูลถูกเปลี่ยนแปลง"
        );
      }
    } catch (error) {
      console.error("❌ อัปเดตล้มเหลว:", error);
      getAlert("FAILED", error.message || "เกิดข้อผิดพลาดในการอัปเดต");
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  console.debug("currentItems", currentItems);

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center mb-0">
        <Breadcrumbs
          page={name}
          items={[
            { name: "จัดซื้อสินค้า", url: "/purchase" },
            { name: name, url: "/purchase-request" },
          ]}
        />
      </div>
      <div className="row" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <div className="col-12">
          <div
            className="p-3 rounded-3 my-2 shadow-lg"
            style={{ backgroundColor: "#ffff" }}
          >
            <div className="row  mb-2">
              <div className="col-3">
                <div className="navbar-form nav-search">
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="ค้นหาเอกสาร..."
                      className="form-control"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-7 d-flex">
                <DateFilterTable
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  selectedDateType={selectedDateType}
                  setSelectedDateType={setSelectedDateType}
                  handleSearch={handleSearch}
                  allDates={allDates}
                  setAllDates={setAllDates}
                />
              </div>
              <div className="col-2 d-flex justify-content-end">
                <button
                  onClick={onPageInsert}
                  className="btn btn-warning text-white ms-2"
                  type="button"
                >
                  สร้าง{name} <i className="fa fa-plus"></i>
                </button>
              </div>
            </div>
            <div
              className="p-1 px-3 rounded-3 shadow-sm"
              style={{ backgroundColor: "#f5f7fd" }}
            >
              <div className="row mb-2">
                <small
                  className="d-block p-1 fw-bold"
                  style={{
                    borderLeft: "5px solid #EF6C00",
                    borderRadius: "3px",
                  }}
                >
                  เรียงลำดับตาม
                </small>

                <div className="col-1 d-flex justify-content-between">
                  <div className="radio-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      checked={sortField === "Doc_No"}
                      onChange={() => setSortField("Doc_No")}
                    />
                    <label className="text-black">เลขที่เอกสาร</label>
                  </div>
                </div>
                <div className="col-1 d-flex justify-content-between">
                  <div className="radio-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      checked={sortField === "Doc_Date"}
                      onChange={() => setSortField("Doc_Date")}
                    />
                    <label className="text-black">วันที่เอกสาร</label>
                  </div>
                </div>
                <div className="col-1 d-flex justify-content-between">
                  <div className="radio-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      checked={sortField === "Created_Date"}
                      onChange={() => setSortField("Created_Date")}
                    />
                    <label className="text-black">วันที่สร้าง</label>
                  </div>
                </div>
                <div className="col-1 d-flex justify-content-between">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleSort}
                    style={{
                      cursor: "pointer",
                      color: "black",
                      backgroundColor: "transparent",
                    }}
                  >
                    <i
                      className={`fas fa-arrow-${
                        sortOrder === "asc" ? "up" : "down"
                      }`}
                    ></i>{" "}
                    Sort
                  </button>
                </div>
                <div className="col-8 d-flex justify-content-end">
                  <button
                    onClick={handleClear}
                    className="btn btn-danger text-white"
                    type="button"
                    style={{ padding: " 0.9rem" }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
          <DataTable
            currentItems={currentItems}
            onRowSelected={onRowSelected}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            dataMasterList={filteredItems}
            itemsPerPage={itemsPerPage}
            updateStatus={updateStatus}
            fieldMappings={{
              no: "Doc_No",
              typeName: "DocType_Name",
              statusName: "DocStatus_Name",
              statusColor: "DocStatus_Colour",
              date: "Created_Date",
              apCode: "AP_Code",
              apName: "AP_Name",
              dueDate: "Doc_DueDate",
              netTotal: "NetTotal",
              createdBy: "Created_By_Name",
              updatedBy: "Update_By_Name",
              File_Name: "File_Name",
            }}
            getReport={(docNo) => {
              const prefix = docNo.match(/^[A-Z]+/g)?.[0] || "";
              const reportType =
                prefix === "POM"
                  ? "PO_OUT"
                  : prefix === "POL"
                  ? "PO_IN"
                  : "PO_OUT";
              return getReport(reportType, docNo);
            }}
          />
        </div>
      </div>
    </>
  );
}

export default Main;
